// A build-time flag with a per-browser preview override, the pattern
// navigation v2 introduced (src/lib/navV2.ts) and the Scan CTA reuses
// (src/lib/scanCta.ts):
//  - the build default, from a VITE_ variable inlined at build time;
//  - a localStorage override ("1" / "0") that wins over it in both
//    directions;
//  - `?<param>=1|0|clear` on any page writes or removes that override, so a
//    phone can opt in without devtools.
// Read once per page load and cached.

const ON = '1'
const OFF = '0'
const CLEAR = 'clear'

export interface PreviewFlag {
  enabled: () => boolean
  /** Test seam: forget the cached answer so the next read re-evaluates. */
  reset: () => void
}

/** True for a VITE_ value that turns a flag on: exactly "1" or "true". */
export function envOn(value: unknown): boolean {
  return value === ON || value === 'true'
}

export function createPreviewFlag(storageKey: string, queryParam: string, buildDefault: boolean): PreviewFlag {
  let cached: boolean | null = null

  const applyQueryOverride = () => {
    if (typeof window === 'undefined') return
    try {
      const value = new URLSearchParams(window.location.search).get(queryParam)
      if (value === ON || value === OFF) window.localStorage.setItem(storageKey, value)
      else if (value === CLEAR) window.localStorage.removeItem(storageKey)
    } catch {
      // storage disabled: the build default stands
    }
  }

  const readOverride = (): boolean | null => {
    if (typeof window === 'undefined') return null
    try {
      const raw = window.localStorage.getItem(storageKey)
      if (raw === ON) return true
      if (raw === OFF) return false
    } catch {
      // storage disabled
    }
    return null
  }

  return {
    enabled: () => {
      if (cached !== null) return cached
      applyQueryOverride()
      cached = readOverride() ?? buildDefault
      return cached
    },
    reset: () => { cached = null },
  }
}
